/**
 * Details tab — contact block (editable), stage / shop selects, role and
 * source facts, application answers, the Resume card and what was extracted
 * from it. Identical in the drawer and on `/leads/:id`.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useApiClient } from '@/lib/api-client';
import { useShops } from '@/lib/shop-store';
import { fullDateTime } from '@/lib/time';
import { errorMessage, useToast } from '@/components/Toast';
import { LEADS_KEY, leadKey, patchLead } from '@/features/pipeline/api';
import { useMoveLead } from '@/features/pipeline/use-move-lead';
import { jsonObject, resumeExtraction, roleTakesAssessment, type LeadDetail, type Stage } from '@/features/pipeline/types';
import { qualificationMeta } from '@/features/pipeline/LeadCard';
import { humanizeReasons, leadReasons } from '@/features/screening/reasons';
import { useScreeningConfigs } from '@/features/screening/screening-api';
import { AssessmentCard } from './AssessmentCard';
import { ContactBlock } from './ContactBlock';
import { ResumeCard } from './ResumeCard';
import { ResumeInsights } from './ResumeInsights';

const selectClass =
  'mt-1 block min-h-10 w-full rounded-md border border-gray-300 bg-white px-2.5 text-sm text-gray-900';

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex gap-3 py-1.5 text-[13px]">
      <dt className="w-24 shrink-0 text-muted">{label}</dt>
      <dd className="min-w-0 flex-1 break-words text-gray-900">{children}</dd>
    </div>
  );
}

/** How many reasons the Details tab shows before pointing at AI Screen for the rest. */
export const DETAILS_REASON_LIMIT = 3;

export function LeadDetailsTab({
  lead,
  stages,
  onShowScreening,
}: {
  lead: LeadDetail;
  stages: Stage[];
  /** Switches to the AI Screen tab ("+N more in AI Screen"). */
  onShowScreening?: () => void;
}) {
  const client = useApiClient();
  const qc = useQueryClient();
  const toast = useToast();
  const { data: shops } = useShops();
  const move = useMoveLead();
  // Same query (and cache) as the AI Screen tab; without it the reasons fall back to the question keys.
  const { data: configs } = useScreeningConfigs();
  const utm = jsonObject(lead.utm_json);
  const answers = jsonObject(lead.answers_json);
  const qual = qualificationMeta(lead.qualification);
  const reasons = humanizeReasons(leadReasons(lead), configs?.[lead.role.slug] ?? null);
  const hiddenReasons = Math.max(0, reasons.length - DETAILS_REASON_LIMIT);
  const extracted = resumeExtraction(lead);

  const assignShop = useMutation({
    mutationFn: (shop_id: string | null) => patchLead(client, lead.id, { shop_id }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: leadKey(lead.id) });
      void qc.invalidateQueries({ queryKey: LEADS_KEY });
    },
    onError: (err) => toast.push({ kind: 'error', title: 'Could not assign shop', message: errorMessage(err) }),
  });

  const onMove = (toStageId: string) => {
    if (!toStageId || toStageId === lead.stage_id) return;
    move.mutate(
      { leadId: lead.id, toStageId },
      { onError: (err) => toast.push({ kind: 'error', title: 'Move failed', message: errorMessage(err) }) },
    );
  };

  return (
    <div className="space-y-5">
      <ContactBlock leadId={lead.id} candidate={lead.candidate} />

      <div className="grid gap-3 sm:grid-cols-2">
        <label className="block text-[13px]">
          <span className="font-medium text-gray-800">Stage</span>
          <select aria-label="Move to stage" value={lead.stage_id} disabled={move.isPending} onChange={(e) => onMove(e.target.value)} className={selectClass}>
            {stages.map((s) => (
              <option key={s.id} value={s.id}>{s.name}</option>
            ))}
          </select>
        </label>
        <label className="block text-[13px]">
          <span className="font-medium text-gray-800">Shop</span>
          <select aria-label="Assign shop" value={lead.shop_id ?? ''} disabled={assignShop.isPending} onChange={(e) => assignShop.mutate(e.target.value || null)} className={selectClass}>
            <option value="">Unassigned</option>
            {(shops ?? []).map((s) => (
              <option key={s.id} value={s.id}>{s.name}</option>
            ))}
          </select>
        </label>
      </div>

      <dl className="divide-y divide-gray-100 rounded-lg border border-gray-200 bg-white px-3">
        <Field label="Phone">{lead.candidate.phone_e164 || '—'}</Field>
        <Field label="Email">{lead.candidate.email || '—'}</Field>
        <Field label="Role">
          {lead.role.title}
          {lead.alias_title && lead.alias_title !== lead.role.title && (
            <span className="text-muted"> · applied as {lead.alias_title}</span>
          )}
        </Field>
        <Field label="Screening">
          {/* Inline, not inline-flex: on a phone the verdict wraps as prose instead of folding around its own dot (Stage 7 QA). */}
          <span className={`mr-1.5 inline-block h-2 w-2 rounded-full align-middle ${qual.dot}`} aria-hidden />
          {qual.label}
          {reasons.length > 0 && (
            <ul className="mt-1 list-disc space-y-0.5 pl-4 text-muted" aria-label="Screening reasons" data-testid="screening-reasons">
              {reasons.slice(0, DETAILS_REASON_LIMIT).map((r, i) => (
                <li key={i}>{r}</li>
              ))}
            </ul>
          )}
          {hiddenReasons > 0 &&
            (onShowScreening ? (
              <button
                type="button"
                onClick={onShowScreening}
                className="mt-0.5 inline-flex min-h-10 items-center rounded-sm font-medium text-navy underline-offset-2 hover:underline"
              >
                +{hiddenReasons} more in AI Screen
              </button>
            ) : (
              <span className="mt-1 block text-muted">+{hiddenReasons} more in AI Screen</span>
            ))}
        </Field>
        <Field label="Source">{lead.source || '—'}</Field>
        {lead.campaign && <Field label="Campaign">{lead.campaign}</Field>}
        {Object.entries(utm).map(([k, v]) => (
          <Field key={k} label={k.replace(/^utm_/, 'UTM ')}>{String(v)}</Field>
        ))}
        <Field label="Applied">{fullDateTime(lead.created_at)}</Field>
      </dl>

      <section>
        <h3 className="mb-1.5 text-[13px] font-semibold text-gray-900">Application answers</h3>
        {Object.keys(answers).length === 0 ? (
          <p className="text-[13px] text-muted">No answers were captured with this application.</p>
        ) : (
          <dl className="divide-y divide-gray-100 rounded-lg border border-gray-200 bg-white px-3">
            {Object.entries(answers).map(([k, v]) => (
              <Field key={k} label={k.replace(/_/g, ' ')}>
                {typeof v === 'object' && v !== null ? JSON.stringify(v) : String(v)}
              </Field>
            ))}
          </dl>
        )}
      </section>

      {roleTakesAssessment(lead.role) && <AssessmentCard leadId={lead.id} assessment={lead.assessment ?? null} />}

      <ResumeCard leadId={lead.id} resume={lead.resume ?? null} />

      {extracted && <ResumeInsights data={extracted} />}
    </div>
  );
}
